import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    toggleAddModal,
    setPage,
    setSearchValue,
    setKeyword,
} from '../../store/organizersSlice';
import {
    getHeaders,
    headerWidths,
    deleteSelected,
} from './organizersTableConfig.js';
import { ReactComponent as AddIcon } from '../../components/icons/add-icon.svg';
import { ReactComponent as PersonalCard } from '../../components/icons/personalcard.svg';
import { ReactComponent as SearchIcon } from '../../components/icons/search.svg';
import { ReactComponent as Trash } from '../../components/icons/trash.svg';
import { fetchOrganizers, mapOrganizersToRows, resolveOrganizerToneClass } from './organizersTableService.js';
import Table from '../../components/list2/table';
import Menu from '../../components/menu/menu';
import Content from '../../components/content/content';
import Pagination from '../../components/components/pagination/pagination.jsx';
import UniversalLineWithComponents from '../../components/components/universal_line_with_components/universalLineWithComponents.jsx';
import CustomButton from '../../components/components/custom_button/customButton.jsx';
import CustomInput from '../../components/components/custom_input/customInput.jsx';
import OrganizerDetailPanel from '../../components/components/organizer_detail_panel/OrganizerDetailPanel.jsx';
import OrganizerEditPanel from '../../components/components/organizer_edit_panel/OrganizerEditPanel.jsx';
import AddOrganizerModal from './AddOrganizerModal.jsx';

import './Organizers.css';

function OrganizerStatusToggle({ value, onChange }) {
    const options = [
        { key: 'NOTCONFIRMED', label: 'Niepotwierdzeni' },
        { key: 'Active', label: 'Aktywni' },
        { key: 'PENDING', label: 'Oczekujący' },
        { key: 'ALL', label: 'Wszyscy' },
    ];

    const currentIndex = Math.max(0, options.findIndex((option) => option.key === value));
    const currentOption = options[currentIndex];
    const nextOption = options[(currentIndex + 1) % options.length];
    const toneClass = resolveOrganizerToneClass(currentOption.key);

    return (
        <button
            type="button"
            className={`organizer-status-toggle-btn organizer-status ${toneClass}`}
            onClick={() => onChange(nextOption.key)}
            aria-label={`Filtr statusu: ${currentOption.label}`}
            title={`Filtr: ${currentOption.label} (kliknij, aby przełączyć)`}
        >
            {currentOption.label}
        </button>
    );
}

function Organizers() {
    const dispatch = useDispatch();
    const totalPages = useSelector((state) => state.organizers.maxPages);
    const currentPage = useSelector((state) => state.organizers.currentPage);
    const sortBy = useSelector((state) => state.organizers.sortBy);
    const sortDirection = useSelector((state) => state.organizers.sortDirection);
    const checkedItems = useSelector((state) => state.organizers.checkedItems);
    const keyword = useSelector((state) => state.organizers.keyword);
    const searchValue = useSelector((state) => state.organizers.searchValue);
    const needRefresh = useSelector((state) => state.organizers.needRefresh);
    const detailPanelOpen = useSelector((state) => state.organizers.detailPanelOpen);
    const addModalOpen = useSelector((state) => state.organizers.addModalOpen);
    const editModalOpen = useSelector((state) => state.organizers.editModalOpen);

    const [rawOrganizers, setRawOrganizers] = useState([]);
    const [filterQuery, setFilterQuery] = useState('ALL');
    const sortArrowColorActive = '#060606';
    const sortArrowColorInactive = '#06060650';

    const rows = useMemo(() => {
        return mapOrganizersToRows(rawOrganizers, checkedItems, dispatch);
    }, [rawOrganizers, checkedItems, dispatch]);

    const handleToggleAddModal = () => {
        dispatch(toggleAddModal());
    };

    useEffect(() => {
        const controller = new AbortController();
        fetchOrganizers(currentPage, sortBy, sortDirection, keyword, null, { filterQuery }, dispatch, setRawOrganizers, {
            signal: controller.signal,
        });

        return () => {
            controller.abort();
        };
    }, [currentPage, sortBy, sortDirection, keyword, filterQuery, needRefresh, dispatch]);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        dispatch(setSearchValue(value));
        if (value.length >= 3) {
            dispatch(setKeyword(value));
        } else {
            dispatch(setKeyword(''));
        }
    };

    const handleDeleteSelected = () => {
        deleteSelected(checkedItems, dispatch);
    };

    const handleFilterQueryChange = (nextFilterQuery) => {
        setFilterQuery(nextFilterQuery);
        dispatch(setPage(1));
    };

    const headers = getHeaders(
        sortBy,
        sortDirection,
        dispatch,
        sortArrowColorActive,
        sortArrowColorInactive,
        rawOrganizers,
        checkedItems
    );

    const currentColumnWidths = headerWidths;
    const paginationProps = {
        reduxSelector: (state) => state.organizers.currentPage,
        reduxAction: setPage,
        totalPages,
        maxVisibleButtons: 5,
    };
    const footerRightSide = [
        {
            component: Pagination,
            props: paginationProps,
        },
    ];
    const searchRightSide = [
        {
            component: OrganizerStatusToggle,
            props: {
                value: filterQuery,
                onChange: handleFilterQueryChange,
            },
        },
        {
            component: CustomInput,
            props: {
                icon: SearchIcon,
                placeholder: 'Wyszukaj',
                hoverClass: 'hover-effect',
                borders: 'LRBT',
                bordersClassPrefix: 'border-black-',
                baseClass: 'custom-input',
                activeClass: '',
                value: searchValue,
                onChange: handleSearchChange,
            },
        },
    ];
    const footerLeftSide = [
        {
            component: CustomButton,
            props: {
                icon: Trash,
                name: 'Usuń zaznaczone',
                baseClass: 'custom-button-white',
                bordersClassPrefix: 'border-black-',
                hoverClass: 'hover-effect',
                activeClass: '',
                borders: 'LRBT',
                onClick: handleDeleteSelected,
                toggleActive: false,
            },
        },
    ];

    return (
        <div className="organizers-page">
            {addModalOpen && <AddOrganizerModal />}
            <Menu />
            <Content>
                <div className="organizers-header">
                    <div className="organizers-tabs-row">
                        <div className="organizers-card-title">
                            <PersonalCard className="organizers-card-title-icon" />
                            Organizatorzy
                        </div>
                        <button className="add-organizer-button" onClick={handleToggleAddModal}>
                            <AddIcon className="add-organizer-icon" />
                            Dodaj organizatora
                        </button>
                    </div>
                </div>

                <div className="table-container">
                    <UniversalLineWithComponents
                        leftSide={[]}
                        rightSide={searchRightSide}
                    />

                    <Table
                        headers={headers}
                        rows={rows}
                        columnWidths={currentColumnWidths}
                        cellClass2="cell-actions"
                        cellClass2ConditionList={[1]}
                    />

                    <UniversalLineWithComponents
                        leftSide={footerLeftSide}
                        rightSide={footerRightSide}
                    />
                </div>
            </Content>

            {detailPanelOpen && (
                <OrganizerDetailPanel allOrganizers={rawOrganizers} />
            )}

            {editModalOpen && (
                <OrganizerEditPanel allOrganizers={rawOrganizers} />
            )}
        </div>
    );
}

export default Organizers;
